import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';
import { filter, skip, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';

import { UserAccountService } from '../../../services/user-account/user-account.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { WebCaptureService } from '../../../services/web-capture/web-capture.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-web-capture-search',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    HeaderComponent,
    SidebarComponent,
    PaginatorComponent,
    RestrictedAccessWarningDirective, CreditsLabelPipe],
  templateUrl: './web-capture-search.component.html',
  styleUrl: './web-capture-search.component.css',
})
export class WebCaptureSearchComponent implements OnInit, OnDestroy {
  availableCredits: number | null = null;
  creditsLoading = true;
  captureUrl = '';
  enableCrawl = false;
  crawlMaxPages = 15;
  crawlPageOptions = [
    { value: 10 },
    { value: 15 },
    { value: 25 },
    { value: 40 },
  ];
  keywords = '';
  isServiceDisabled = false;
  searchCreditCost: number | null = null;
  searchLoading = false;
  loadingRecentResults = false;
  recentResults: any[] = [];
  recentResultsPaged: any[] = [];
  recentResultsCount = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage = false;
  recentResultsHasNextPage = false;
  recentResultsSelectAll = { isSelected: false };
  jobIds: string[] = [];
  loadingJobIds = false;
  activeWebCaptureId: string | null = null;
  activeCaptureStep = 0;

  baseCaptureSteps = [
    { icon: 'fa-camera', label: 'Capturing full-page screenshot' },
    { icon: 'fa-code', label: 'Preserving HTML source' },
    { icon: 'fa-lock', label: 'Inspecting SSL/TLS certificate' },
    { icon: 'fa-network-wired', label: 'Resolving DNS & WHOIS records' },
    { icon: 'fa-file-alt', label: 'Reading HTTP response headers' },
    { icon: 'fa-shield-alt', label: 'Running phishing / threat scan' },
    { icon: 'fa-crosshairs', label: 'Extracting selectors & indicators' },
    { icon: 'fa-fingerprint', label: 'Computing SHA-256 integrity hashes' },
  ];
  crawlCaptureStep = {
    icon: 'fa-layer-group',
    label: 'Crawling & mirroring entire site',
  };

  get captureSteps() {
    return this.enableCrawl
      ? [...this.baseCaptureSteps, this.crawlCaptureStep]
      : this.baseCaptureSteps;
  }

  get isCapturing(): boolean {
    return this.searchLoading || this.loadingJobIds;
  }

  selectedFilterType = 'url';
  filterTypes = [
    { label: 'URL', value: 'url' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];
  urlFilter = '';
  notesFilter = '';
  startDateFilter = '';
  endDateFilter = '';

  private routerEventsSubscription?: Subscription;
  private allJobResultsSubscription?: Subscription;
  private captureStepInterval?: ReturnType<typeof setInterval>;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private userAccountService: UserAccountService,
    private searchPollingService: SearchPollingService,
    private searchServiceStatusService: SearchServiceStatusService,
    private webCaptureService: WebCaptureService,
  ) {}

  ngOnInit(): void {
    this.searchServiceStatusService.searchServices$.subscribe((services) => {
      if (services) {
        this.isServiceDisabled = !services.web_capture;
      }
    });

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'webCaptureCreditCost',
      );
    });

    this.getAvailableCredits();
    this.getRecentResults();
    this.subscribeToJobResultsUpdates();

    this.routerEventsSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (
          event.url === '/web-capture' ||
          event.url.startsWith('/web-capture?')
        ) {
          this.getAvailableCredits();
          this.getRecentResults();
        }
      });
  }

  ngOnDestroy(): void {
    this.routerEventsSubscription?.unsubscribe();
    this.allJobResultsSubscription?.unsubscribe();
    this.stopCaptureStepAnimation();
  }

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }
    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );
    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((r: any) => r.isSelected)
      .map((r: any) => r.web_capture_id);
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
      },
      error: (error) => {
        console.error(error);
        const errorMessage =
          error?.error?.message_key ||
          error?.error?.message ||
          error?.message ||
          error?.statusText ||
          'Failed to load available credits';
        this.toastr.error(errorMessage, 'Credits Load Failed');
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
    });
  }

  startCapture() {
    const url = (this.captureUrl || '').trim();
    if (!url) {
      this.toastr.warning('Please enter a URL to capture.');
      return;
    }

    this.searchLoading = true;
    const keywordList = this.keywords
      .split(/[\n,]/)
      .map((k) => k.trim())
      .filter(Boolean);

    this.webCaptureService
      .captureUrl({
        url,
        crawl: this.enableCrawl,
        crawl_max_pages: this.enableCrawl ? this.crawlMaxPages : undefined,
        crawl_max_depth: 2,
        keywords: keywordList,
      })
      .subscribe({
        next: (response: any) => {
          const jobId = response?.data?.job_id;
          const webCaptureId = response?.data?.web_capture_id;
          if (jobId) {
            this.jobIds = [jobId];
            this.activeWebCaptureId = webCaptureId || null;
            this.loadingJobIds = true;
            this.startCaptureStepAnimation();
            this.searchPollingService.startPolling(
              this.jobIds,
              (ids) => this.webCaptureService.getSearchResults(ids),
              10 * 60 * 1000,
              5000,
            );
          }
          this.captureUrl = '';
          this.getRecentResults();
          this.searchLoading = false;
        },
        error: (err) => {
          this.searchLoading = false;
          this.toastr.error(
            err?.error?.message || 'Unable to start Web Capture.',
          );
        },
      });
  }

  subscribeToJobResultsUpdates() {
    this.allJobResultsSubscription?.unsubscribe();
    this.allJobResultsSubscription = this.searchPollingService
      .getAllJobResults()
      .pipe(skip(1))
      .subscribe((map) => {
        this.recentResultsPaged.forEach((result: any) => {
          if (result.job_id) {
            const updated = map.get(result.job_id);
            if (updated) {
              result.done = updated.done;
              result.capture_status = updated.status;
            }
          }
        });
        if (this.loadingJobIds && this.jobIds.length) {
          const status = map.get(this.jobIds[0]);
          if (status?.done) {
            this.onCaptureComplete(status);
          }
        }
      });
  }

  onCaptureComplete(status: any) {
    this.activeCaptureStep = this.captureSteps.length;
    this.loadingJobIds = false;
    this.stopCaptureStepAnimation();
    this.getAvailableCredits();
    this.getRecentResults();

    if (status?.status === 'completed' && this.activeWebCaptureId) {
      this.toastr.success('Capture complete — evidence preserved');
      this.router.navigate([
        '/web-capture-result',
        this.activeWebCaptureId,
      ]);
      return;
    }

    if (status?.status === 'failed') {
      this.toastr.error(status?.message || 'Capture failed. Please try again.');
    }
    this.activeWebCaptureId = null;
  }

  startCaptureStepAnimation() {
    this.stopCaptureStepAnimation();
    this.activeCaptureStep = 0;
    this.captureStepInterval = setInterval(() => {
      const max = this.captureSteps.length - 1;
      if (this.activeCaptureStep < max) {
        this.activeCaptureStep += 1;
      }
    }, 1600);
  }

  stopCaptureStepAnimation() {
    if (this.captureStepInterval) {
      clearInterval(this.captureStepInterval);
      this.captureStepInterval = undefined;
    }
    this.activeCaptureStep = 0;
  }

  getRecentResults() {
    this.loadingRecentResults = true;
    let searchKey: string | undefined;
    let searchValue: string | undefined;
    let startDate: string | undefined;
    let endDate: string | undefined;

    if (this.selectedFilterType === 'url' && this.urlFilter) {
      searchKey = 'url';
      searchValue = this.urlFilter;
    } else if (this.selectedFilterType === 'notes' && this.notesFilter) {
      searchKey = 'notes';
      searchValue = this.notesFilter;
    } else if (this.selectedFilterType === 'date-added') {
      searchKey = 'created_on';
      startDate = this.startDateFilter;
      endDate = this.endDateFilter;
    }

    this.webCaptureService
      .getHistory(
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        searchKey,
        searchValue,
        startDate,
        endDate,
      )
      .subscribe({
        next: (response: any) => {
          this.recentResults = response?.data?.data_list || [];
          this.recentResultsCount = response?.data?.total_items || 0;
          this.recentResultsPaged = this.recentResults.map((r: any) => ({
            ...r,
            isSelected: false,
          }));
          this.recentResultsHasPreviousPage =
            response?.data?.has_previous ?? false;
          this.recentResultsHasNextPage =
            response?.data?.has_next ?? false;
          this.loadingRecentResults = false;
        },
        error: () => {
          this.loadingRecentResults = false;
        },
      });
  }

  openResult(row: any) {
    if (!row?.web_capture_id) return;
    this.router.navigate(['/web-capture-result', row.web_capture_id]);
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.getRecentResults();
  }

  onFilterTypeChange() {
    this.recentResultsCurrentPage = 1;
    this.getRecentResults();
  }

  onDeleteButtonClick() {
    const ids = this.selectedRecentResults;
    if (!ids.length) {
      this.toastr.warning('Select at least one capture to delete.');
      return;
    }
    this.webCaptureService.deleteHistory(ids).subscribe({
      next: () => {
        this.toastr.success('Deleted successfully.');
        this.getRecentResults();
      },
      error: (err) =>
        this.toastr.error(err?.error?.message || 'Delete failed.'),
    });
  }

  statusLabel(row: any): string {
    if (!row?.done) return 'Processing';
    return row.capture_status === 'completed' ? 'Completed' : 'Failed';
  }

  riskClass(row: any): string {
    const status = this.statusLabel(row).toLowerCase();
    if (status === 'completed') return 'status-success';
    if (status === 'processing') return 'status-pending';
    return 'status-failed';
  }

  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + ' .....';
  }
}
